import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  Loading,
  MessageBanner,
  Pane,
  PaneFooter,
  Paneset,
  Row,
} from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import stripesFinalForm from '@folio/stripes/final-form';
import { TemplateEditor } from '@folio/stripes-template-editor';
import LocalizedTextField from '../../components/common/LocalizedTextField';
import scheduleTokens, { SCHEDULE_CATEGORY } from '../../components/scheduled/scheduleTokens';
import ScheduleTokensList from '../../components/scheduled/ScheduleTokensList';

function EmailTemplateForm({ handleSubmit, pristine, submitting, canManage = false, form, isLoading }) {
  if (isLoading) return <Loading />;

  const renderFooter = () => (
    <PaneFooter
      renderEnd={
        <Button
          buttonStyle="primary"
          disabled={pristine || submitting || !canManage}
          marginBottom0
          type="submit"
        >
          <FormattedMessage id="stripes-core.button.save" defaultMessage="Save" />
        </Button>
      }
      renderStart={
        <Button
          buttonStyle="default"
          disabled={pristine || submitting || !canManage}
          marginBottom0
          onClick={() => form.reset()}
        >
          <FormattedMessage id="stripes-components.cancel" defaultMessage="Cancel" />
        </Button>
      }
    />
  );

  return (
    <form id="form-email-template" onSubmit={handleSubmit} style={{ width: 'inherit' }}>
      <Paneset>
        <Pane
          fluidContentWidth
          footer={renderFooter()}
          paneTitle={
            <FormattedMessage
              id="ui-reports.settings.emailTemplate.paneTitle"
              defaultMessage="Email Templates"
            />
          }
        >
          {/* Placeholders help */}
          <MessageBanner type="informational" style={{ marginBottom: '1.5rem' }}>
            <FormattedMessage
              id="ui-reports.settings.emailTemplate.placeholdersHelp"
              defaultMessage="Available placeholders: {reportName} — report name, {date} — execution date, {format} — file format, {records} — number of records"
            />
          </MessageBanner>

          <Row>
            <Col xs={12}>
              <FieldArray name="emailSubject">
                {({ fields }) => (
                  <LocalizedTextField
                    fields={fields}
                    fieldLabel={
                      <FormattedMessage
                        id="ui-reports.settings.emailTemplate.subject"
                        defaultMessage="Email Subject"
                      />
                    }
                    isRequired
                  />
                )}
              </FieldArray>
            </Col>
          </Row>

          <Row style={{ marginTop: '1.5rem' }}>
            <Col xs={12}>
              <FieldArray
                name="emailBody"
                component={LocalizedTextField}
                fieldLabel={
                  <FormattedMessage
                    id="ui-reports.settings.emailTemplate.body"
                    defaultMessage="Email Body"
                  />
                }
                fieldComponent={TemplateEditor}
                rowStyle={{ marginBottom: '1.5rem' }}
                fieldComponentProps={{
                  tokens: scheduleTokens,
                  tokensList: ScheduleTokensList,
                  previewModalHeader: (
                    <FormattedMessage
                      id="ui-reports.scheduledReports.emailBody.preview"
                      defaultMessage="Email Body Preview"
                    />
                  ),
                  selectedCategory: SCHEDULE_CATEGORY,
                }}
              />
            </Col>
          </Row>
        </Pane>
      </Paneset>
    </form>
  );
}

EmailTemplateForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine:     PropTypes.bool,
  submitting:   PropTypes.bool,
  form:         PropTypes.object.isRequired,
  isLoading:    PropTypes.bool,
};

export default stripesFinalForm({
  navigationCheck: true,
  subscription: { values: true },
  enableReinitialize: true,
  initialValuesEqual: (a, b) => a === b,
  mutators: { ...arrayMutators },
})(EmailTemplateForm);
